/* NATURTREU – Expertenrat
 * Slider auf Basis von nativem CSS scroll-snap.
 * Bewusst ohne Flickity (siehe MEMORY.md / ERRORS.md).
 *
 * Das JS liefert drei Dinge, die nativ fehlen:
 *   1. Maus-Drag  – Scroll-Container lassen sich am Desktop nicht ziehen.
 *   2. Rand-Bleed – exakt berechnet, damit rechts kein weisser Streifen bleibt.
 *   3. Pfeile     – inkl. Disabled-State an Anfang und Ende.
 */
class ExpertenratSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-expertenrat-viewport]');
    this.prevButton = this.querySelector('[data-expertenrat-prev]');
    this.nextButton = this.querySelector('[data-expertenrat-next]');

    if (!this.viewport) return;

    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.updateButtons = this.updateButtons.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

    this.viewport.addEventListener('scroll', this.updateButtons, { passive: true });
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('click', this.onClickCapture, true);
    window.addEventListener('resize', this.onResize);

    this.applyBleed();
    this.updateButtons();
  }

  disconnectedCallback() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    if (this.viewport) {
      this.viewport.removeEventListener('scroll', this.updateButtons);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('click', this.onClickCapture, true);
    }
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.clearTimeout(this.resizeTimer);
  }

  /* --- Rand-Bleed ------------------------------------------------------- */

  /* Zieht die rechte Kante des Sliders exakt an den Bildschirmrand.
     documentElement.clientWidth statt 100vw, weil 100vw die Scrollbar
     mitzaehlt und dadurch horizontalen Seiten-Overflow erzeugt. */
  applyBleed() {
    this.viewport.style.marginRight = '0px';

    /* Nur mobil. Auf Desktop wuerde der Track sonst mit der Bildschirmbreite
       mitwachsen – auf sehr breiten Screens passen dann alle Cards hinein,
       der Anriss verschwindet und die Pfeile blenden sich aus. */
    if (window.matchMedia('(min-width: 750px)').matches) return;

    const rect = this.viewport.getBoundingClientRect();
    const bleed = document.documentElement.clientWidth - rect.right;
    if (bleed > 0) {
      this.viewport.style.marginRight = `-${Math.round(bleed)}px`;
    }
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      this.applyBleed();
      this.updateButtons();
    }, 100);
  }

  /* --- Maus-Drag -------------------------------------------------------- */

  onPointerDown(event) {
    // Touch macht der Browser selbst, Buttons sollen klickbar bleiben
    if (event.pointerType === 'touch') return;
    if (event.target.closest('a, button')) return;
    if (event.button !== 0) return;

    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.viewport.scrollLeft;
    this.hasDragged = false;

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    const delta = event.clientX - this.dragStartX;

    if (!this.hasDragged) {
      if (Math.abs(delta) < 5) return;
      this.hasDragged = true;
      this.viewport.classList.add('is-dragging');
    }

    event.preventDefault();
    this.viewport.scrollLeft = this.dragStartScroll - delta;
  }

  onPointerUp() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    this.viewport.classList.remove('is-dragging');
    this.pointerId = null;
    this.updateButtons();
  }

  /* Klick nach einem Drag schlucken, damit Links in der Card nicht auslösen */
  onClickCapture(event) {
    if (!this.hasDragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  }

  /* --- Pfeile ----------------------------------------------------------- */

  /* Scrollweite = Breite einer Card + Gap, aus dem DOM gelesen, damit die
     Werte aus dem CSS nicht doppelt gepflegt werden müssen. */
  getStep() {
    const cell = this.viewport.querySelector('.nt-expertenrat__cell');
    if (!cell) return this.viewport.clientWidth;

    const styles = window.getComputedStyle(this.viewport);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return cell.getBoundingClientRect().width + gap;
  }

  onPrev(event) {
    event.preventDefault();
    this.viewport.scrollBy({ left: -this.getStep(), behavior: 'smooth' });
  }

  onNext(event) {
    event.preventDefault();
    this.viewport.scrollBy({ left: this.getStep(), behavior: 'smooth' });
  }

  updateButtons() {
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    const position = this.viewport.scrollLeft;
    const tolerance = 2;

    // Alles passt ohne Scrollen -> Pfeile ausblenden
    if (maxScroll <= tolerance) {
      this.setAttribute('data-slider-static', '');
      return;
    }
    this.removeAttribute('data-slider-static');

    if (this.prevButton) {
      this.prevButton.setAttribute('aria-disabled', position <= tolerance ? 'true' : 'false');
    }
    if (this.nextButton) {
      this.nextButton.setAttribute('aria-disabled', position >= maxScroll - tolerance ? 'true' : 'false');
    }
  }
}

if (!customElements.get('expertenrat-slider')) {
  customElements.define('expertenrat-slider', ExpertenratSlider);
}